import { useParams } from "react-router-dom";
import { useAccount } from "wagmi";
import { useModal } from "connectkit";
import { Globe, Users } from "lucide-react";
import { FaXTwitter, FaLinkedinIn, FaGithub, FaYoutube, FaInstagram } from "react-icons/fa6";
import { useWriterProfile, useArticles } from "@/hooks/useArticles";
import {
  useIsSubscribed,
  useSubscribe,
  useUnsubscribe,
} from "@/hooks/useSubscription";
import { Avatar } from "@/components/ui/Avatar";
import { ArticleList } from "@/components/article/ArticleList";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";

const SOCIAL_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  twitter: FaXTwitter,
  linkedin: FaLinkedinIn,
  github: FaGithub,
  youtube: FaYoutube,
  instagram: FaInstagram,
  website: Globe,
};

export function WriterProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { data: writer, isLoading: writerLoading } = useWriterProfile(id || "");
  const { data: articles } = useArticles({ writerId: id });
  const { address } = useAccount();
  const { setOpen } = useModal();
  const { data: subscribed } = useIsSubscribed(id || "", address || "");
  const subscribeMutation = useSubscribe();
  const unsubscribeMutation = useUnsubscribe();

  if (writerLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Skeleton className="h-14 w-14 rounded-full mb-4" />
        <Skeleton className="h-6 w-48 mb-2" />
        <Skeleton className="h-4 w-80" />
      </div>
    );
  }

  if (!writer) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-display font-bold">Writer not found</h2>
      </div>
    );
  }

  const socialEntries = Object.entries(writer.socialLinks || {}).filter(
    ([, url]) => url,
  );

  const handleSubscribe = () => {
    if (!id) return;
    if (!address) {
      setOpen(true);
      return;
    }
    if (subscribed) {
      unsubscribeMutation.mutate({ writerId: id, subscriberAddress: address });
    } else {
      subscribeMutation.mutate({ writerId: id, subscriberAddress: address });
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex items-start gap-4 mb-10">
        <Avatar src={writer.avatarUrl} name={writer.displayName} size="lg" />
        <div className="flex-1">
          <h1 className="text-2xl font-display font-bold">
            {writer.displayName}
          </h1>
          {writer.tagline && (
            <p className="text-grey font-body mt-0.5">{writer.tagline}</p>
          )}
          {writer.bio && (
            <p className="text-grey font-body mt-1 text-sm">{writer.bio}</p>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-sm text-beige font-body">
              {writer.articleCount} article
              {writer.articleCount !== 1 ? "s" : ""}
            </span>
            <span className="flex items-center gap-1 text-sm text-beige font-body">
              <Users size={14} />
              {writer.subscriberCount} subscriber
              {writer.subscriberCount !== 1 ? "s" : ""}
            </span>
            {socialEntries.map(([platform, url]) => {
              const Icon = SOCIAL_ICONS[platform] || Globe;
              return (
                <a
                  key={platform}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-grey hover:text-orange transition-colors"
                  title={platform}
                >
                  <Icon size={16} />
                </a>
              );
            })}
          </div>
          <Button
            size="sm"
            variant={subscribed ? "ghost" : "primary"}
            className="mt-3"
            onClick={handleSubscribe}
            loading={subscribeMutation.isPending || unsubscribeMutation.isPending}
          >
            {subscribed ? "Unsubscribe" : "Subscribe"}
          </Button>
        </div>
      </div>

      <h2 className="text-lg font-display font-bold mb-4">Articles</h2>
      <ArticleList articles={articles || []} />
    </div>
  );
}
